import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import axios from 'axios';
import { APIURL } from '../../config';
import moment from 'moment';
import './CaregiverTurnoverChart.css';

const COLORS = {
    total: '#3FA7D6',
    newHires: '#06D6A0',
    departed: '#EF476F',
};

const MONTHS_TO_SHOW = 6;

export default function CaregiverTurnoverChart({ user, comp_id }) {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);
    const [dimensions, setDimensions] = useState(null);
    const observerRef = useRef(null);

    const containerRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (node) {
            const resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const { width, height } = entry.contentRect;
                    setDimensions({
                        width: Math.max(width * 0.8, 200),
                        height: Math.max(height * 0.8, 100),
                    });
                }
            });
            resizeObserver.observe(node);
            observerRef.current = resizeObserver;
        }
    }, []);

    useEffect(() => {
        fetchTurnoverData();
    }, []);

    const fetchTurnoverData = async () => {
        try {
            const res = await axios.get(`${APIURL}/employees`, {
                params: { com_id: comp_id }
            });

            const employees = res.data || [];

            const monthBuckets = {};
            for (let i = 0; i < MONTHS_TO_SHOW; i++) {
                const m = moment().subtract(MONTHS_TO_SHOW - 1 - i, 'months');
                const key = m.format('YYYY-MM');
                monthBuckets[key] = {
                    month: m.format('MMM'),
                    total: 0,
                    newHires: 0,
                    departed: 0,
                };
            }

            const monthKeys = Object.keys(monthBuckets);
            const firstMonth = moment(monthKeys[0], 'YYYY-MM').startOf('month');

            employees.forEach((emp) => {
                const hireDate = emp.emp_join_date ? moment(emp.emp_join_date) : null;
                const termDate = emp.date_of_termination ? moment(emp.date_of_termination) : null;

                monthKeys.forEach((mKey) => {
                    const monthStart = moment(mKey, 'YYYY-MM').startOf('month');
                    const monthEnd = moment(mKey, 'YYYY-MM').endOf('month');

                    // If no hire date, treat as always employed
                    const wasHiredBefore = !hireDate || hireDate.isSameOrBefore(monthEnd);
                    const wasTermedBefore = termDate && termDate.isBefore(monthStart);

                    if (wasHiredBefore && !wasTermedBefore) {
                        monthBuckets[mKey].total += 1;
                    }

                    if (hireDate && hireDate.isBetween(monthStart, monthEnd, null, '[]')) {
                        monthBuckets[mKey].newHires += 1;
                    }

                    if (termDate && termDate.isBetween(monthStart, monthEnd, null, '[]')) {
                        monthBuckets[mKey].departed += 1;
                    }
                });
            });

            setChartData(Object.values(monthBuckets));
        } catch (err) {
            console.error('Error fetching caregiver turnover data:', err);
            setChartData([]);
        } finally {
            setLoading(false);
        }
    };

    const isEmpty = chartData.length === 0 || chartData.every((d) => d.total === 0 && d.newHires === 0 && d.departed === 0);

    return (
        <div className="caregiver-turnover-container">
            <div className="caregiver-turnover-header">
                <span className="caregiver-turnover-header-title">
                    <i className="fas fa-users" style={{ color: '#3FA7D6' }} />
                    Caregiver Turnover
                </span>
            </div>
            {loading ? (
                <div className="caregiver-turnover-loading">
                    <CircularProgress size={32} />
                </div>
            ) : isEmpty ? (
                <div className="caregiver-turnover-empty">
                    <i className="fas fa-user-friends" style={{ fontSize: 28, color: '#ccc' }} />
                    <span>No employee data available</span>
                </div>
            ) : (
                <>
                    <div className="caregiver-turnover-body" ref={containerRef}>
                        {dimensions && <BarChart
                            width={dimensions.width}
                            height={dimensions.height}
                            series={[
                                { data: chartData.map((d) => d.total), label: 'Total Employees', color: COLORS.total },
                                { data: chartData.map((d) => d.newHires), label: 'New Hires', color: COLORS.newHires },
                                { data: chartData.map((d) => d.departed), label: 'Departed', color: COLORS.departed },
                            ]}
                            xAxis={[{ data: chartData.map((d) => d.month), scaleType: 'band' }]}
                            slotProps={{
                                legend: { hidden: true },
                            }}
                            margin={{ top: 10, right: 10, bottom: 24, left: 40 }}
                        />}
                    </div>
                    <div className="caregiver-turnover-legend">
                        <div className="caregiver-turnover-legend-item">
                            <span className="caregiver-turnover-legend-swatch" style={{ backgroundColor: COLORS.total }} />
                            Total Employees
                        </div>
                        <div className="caregiver-turnover-legend-item">
                            <span className="caregiver-turnover-legend-swatch" style={{ backgroundColor: COLORS.newHires }} />
                            New Hires
                        </div>
                        <div className="caregiver-turnover-legend-item">
                            <span className="caregiver-turnover-legend-swatch" style={{ backgroundColor: COLORS.departed }} />
                            Departed
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
